import type { ReactNode } from "react";

const LEXSITE_URL = (import.meta.env.VITE_LEXSITE_URL ?? "http://localhost:5176").replace(/\/$/, "");
const EAP_URL = `${LEXSITE_URL}/early-access`;

export function ClerkNotConfiguredPlaceholder({ mode }: { mode: "sign-in" | "sign-up" }) {
  const label = mode === "sign-in" ? "Sign in" : "Sign up";
  return (
    <div
      role="status"
      aria-label={`${label} unavailable in dev: Clerk not configured`}
      style={{
        background: "#111520",
        border: "1px solid rgba(212,160,82,0.2)",
        borderRadius: 4,
        padding: "32px 36px",
        maxWidth: 400,
        textAlign: "center",
        color: "rgba(242,238,233,0.85)",
        fontFamily: "'DM Sans', system-ui, sans-serif",
        fontSize: "0.9375rem",
        lineHeight: 1.55,
      }}
    >
      <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "1.25rem", color: "#F2EEE9", marginBottom: 12 }}>
        {label}
      </div>
      <div style={{ color: "rgba(242,238,233,0.65)", fontSize: "0.8125rem" }}>
        Clerk is not configured in this dev environment. Set
        {" "}<code style={{ color: "#D4A052" }}>VITE_CLERK_PUBLISHABLE_KEY</code>{" "}
        in <code style={{ color: "#D4A052" }}>lexide/.env</code> to enable the
        sign-in widget. The chrome around this card is what visitors arriving
        from lexiun.ai will see in production.
      </div>
    </div>
  );
}

export const clerkAppearance = {
  variables: {
    colorPrimary: "#D4A052",
    // Dark text/icons on the gold primary button (white-on-gold ≈ 1.9:1 fails WCAG; dark ≈ 8:1)
    colorTextOnPrimaryBackground: "#0B0D11",
    colorBackground: "#111520",
    colorText: "#F2EEE9",
    colorTextSecondary: "rgba(242,238,233,0.7)",
    colorInputBackground: "#0B0D11",
    colorInputText: "#F2EEE9",
    colorNeutral: "#F2EEE9",
    fontFamily: "'DM Sans', system-ui, sans-serif",
    borderRadius: "4px",
  },
} as const;

export function AuthLayout({ children, eapText }: { children: ReactNode; eapText: string }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #0B0D11 0%, #111520 100%)",
        color: "#F2EEE9",
        fontFamily: "'DM Sans', system-ui, sans-serif",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <header style={{ padding: "20px clamp(20px, 5vw, 60px)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
        <a
          href={LEXSITE_URL}
          aria-label="Back to lexiun.ai"
          style={{
            display: "inline-flex",
            alignItems: "center",
            textDecoration: "none",
            lineHeight: 1,
          }}
        >
          <img
            src="/lexiun-wordmark.svg"
            alt="Lexiun"
            style={{ height: "1.625rem", width: "auto", display: "block" }}
          />
        </a>
        <a
          href={LEXSITE_URL}
          style={{
            color: "rgba(242,238,233,0.5)",
            textDecoration: "none",
            fontSize: "0.8125rem",
            letterSpacing: "0.01em",
            transition: "color 0.2s ease",
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLAnchorElement).style.color = "rgba(242,238,233,0.85)";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLAnchorElement).style.color = "rgba(242,238,233,0.5)";
          }}
        >
          ← lexiun.ai
        </a>
      </header>
      <main
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "0 20px 40px",
          gap: 20,
        }}
      >
        {children}
        <a
          href={EAP_URL}
          style={{
            color: "rgba(242,238,233,0.75)",
            fontSize: "0.875rem",
            textDecoration: "none",
            borderBottom: "1px solid rgba(212,160,82,0.4)",
            paddingBottom: 1,
            transition: "color 0.2s ease, border-color 0.2s ease",
          }}
          onMouseEnter={(e) => {
            const s = (e.currentTarget as HTMLAnchorElement).style;
            s.color = "#F2EEE9";
            s.borderColor = "#D4A052";
          }}
          onMouseLeave={(e) => {
            const s = (e.currentTarget as HTMLAnchorElement).style;
            s.color = "rgba(242,238,233,0.75)";
            s.borderColor = "rgba(212,160,82,0.4)";
          }}
        >
          {eapText}
        </a>
      </main>
    </div>
  );
}
